// ./src/components/generic/ColorPickerInput/ColorPickerInput.tsx
//
// A compact color picker styled as a form input — reusable anywhere a user picks
// a color (tags, guest groups, website blocks, …):
// - A preview swatch matching the height of other Mantine inputs (renders solid
//   colors, gradients, and transparency with a checkerboard).
// - "Shuffle" cycles random palette colors; "Pick" opens the editor popover.
// - A value box in the popover to copy the current color or paste any CSS format
//   (hex / rgb / rgba / hsl / named) — normalized on entry.
//
// Two opt-in powers, off by default so entity-color fields (which feed a Mantine
// `color=` prop and must stay a single swatch) keep emitting a plain hex:
//   • allowTransparent — an alpha slider + a Transparent shortcut (emits #RRGGBBAA
//     or "transparent").
//   • allowGradient    — a Solid/Gradient toggle; gradient mode builds a
//     linear/radial CSS gradient with 2+ stops (emits a `linear-gradient(…)` /
//     `radial-gradient(…)` string). Only enable where the value becomes a CSS
//     background.

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Group, Text, InputWrapper, ActionIcon, Tooltip, ColorPicker, Stack, SegmentedControl, NumberInput, TextInput, Button, ColorSwatch } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useTranslation } from "react-i18next";

import { DEFAULT_COLOR_PALETTE } from "@/config/colorPalette";
import { getMostContrastingColor } from "@/utils/colorHelpers";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface ColorPickerInputProps {
  /** Field name for form binding. */
  name?: string;

  /** Current selected value (hex, rgba, or a gradient string). */
  value: string | null | undefined;

  /** Callback when the value changes: (name, value). */
  onChange: (name: string, color: string) => void;

  /** Preset swatches. Defaults to the shared app palette. */
  palette?: readonly string[];

  /** Field label. */
  label?: string;

  /** Description text. */
  description?: string;

  /** Error message. */
  error?: string;

  /** Whether the picker is disabled. */
  disabled?: boolean;

  /** Size variant — matches Mantine input sizes. */
  size?: "sm" | "md" | "lg";

  /** Whether the field is required. */
  required?: boolean;

  /** Hide the hex value on the swatch (for tight/compact layouts). */
  hideValue?: boolean;

  /** Allow an alpha channel + a Transparent shortcut. */
  allowTransparent?: boolean;

  /** Allow building a linear/radial gradient (only for CSS-background fields). */
  allowGradient?: boolean;
}

interface GradientStop {
  color: string;
  position: number;
}

// ============================================
// Constants
// ============================================

const INPUT_HEIGHT = { sm: 36, md: 42, lg: 50 };
const ICON_SIZE = { sm: 28, md: 34, lg: 40 };
const DEFAULT_COLOR = "#6B7280";
// A light/dark checkerboard so transparency reads as "see-through", not white.
const CHECKER =
  "repeating-conic-gradient(var(--mantine-color-gray-4) 0% 25%, transparent 0% 50%) 50% / 12px 12px";

// ============================================
// Color helpers
// ============================================

const isGradient = (v: string | null | undefined): boolean =>
  !!v && (v.startsWith("linear-gradient") || v.startsWith("radial-gradient"));

// Normalize any CSS color the browser understands to { hex, alpha }. Uses a
// canvas 2d context (which resolves named/rgb/hsl to a concrete value) and a
// two-sentinel check to reject inputs the browser could not parse.
let sharedCtx: CanvasRenderingContext2D | null | undefined;
const normalizeColor = (input: string): { hex: string; alpha: number } | null => {
  const s = (input || "").trim();
  if (!s || isGradient(s)) return null;
  if (sharedCtx === undefined) {
    sharedCtx = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;
  }
  if (!sharedCtx) return null;
  sharedCtx.fillStyle = "#000";
  sharedCtx.fillStyle = s;
  const a = sharedCtx.fillStyle;
  sharedCtx.fillStyle = "#fff";
  sharedCtx.fillStyle = s;
  const b = sharedCtx.fillStyle;
  if (a !== b) return null; // invalid — the two sentinels stuck
  const out = a as string;
  if (out.startsWith("#")) return { hex: out.toUpperCase(), alpha: 1 };
  const m = out.match(/rgba?\(([^)]+)\)/i);
  if (!m) return null;
  const parts = m[1].split(",").map((x) => parseFloat(x.trim()));
  const [r, g, bl, al = 1] = parts;
  const hex = `#${[r, g, bl].map((n) => Math.round(n).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
  return { hex, alpha: al };
};

const withAlpha = (hex6: string, alpha: number): string => {
  if (alpha >= 1) return hex6.toUpperCase();
  if (alpha <= 0) return "transparent";
  const aa = Math.round(alpha * 255).toString(16).padStart(2, "0");
  return `${hex6.toUpperCase()}${aa.toUpperCase()}`;
};

// Split a solid value into a 6-digit hex + alpha (handles #RGB, #RRGGBB,
// #RRGGBBAA, "transparent", and any browser-parseable color).
const parseSolid = (v: string | null | undefined): { hex: string; alpha: number } => {
  if (!v || v === "transparent") return { hex: "#FFFFFF", alpha: v === "transparent" ? 0 : 1 };
  if (/^#([0-9a-f]{8})$/i.test(v)) {
    return { hex: v.slice(0, 7).toUpperCase(), alpha: parseInt(v.slice(7, 9), 16) / 255 };
  }
  return normalizeColor(v) ?? { hex: "#FFFFFF", alpha: 1 };
};

const buildGradient = (type: "linear" | "radial", angle: number, stops: GradientStop[]): string => {
  const parts = stops.map((s) => `${s.color} ${s.position}%`).join(", ");
  return type === "linear" ? `linear-gradient(${angle}deg, ${parts})` : `radial-gradient(circle, ${parts})`;
};

const parseGradient = (v: string): { type: "linear" | "radial"; angle: number; stops: GradientStop[] } => {
  const type: "linear" | "radial" = v.startsWith("radial-gradient") ? "radial" : "linear";
  const angle = Number(v.match(/linear-gradient\((\d+)deg/)?.[1] ?? 90);
  const found = v.match(/#[0-9a-fA-F]{3,8}\s+\d+%/g) ?? [];
  const stops: GradientStop[] = found.map((stop) => {
    const [color, pos] = stop.split(/\s+/);
    return { color: color.toUpperCase(), position: parseInt(pos, 10) };
  });
  return {
    type,
    angle,
    stops: stops.length >= 2 ? stops : [
      { color: "#4C6EF5", position: 0 },
      { color: "#F783AC", position: 100 },
    ],
  };
};

const getRandomPaletteColor = (palette: readonly string[], current?: string | null): string => {
  const candidates = current ? palette.filter((c) => c.toUpperCase() !== (current || "").toUpperCase()) : palette;
  const pool = candidates.length > 0 ? candidates : palette;
  return pool[Math.floor(Math.random() * pool.length)] || DEFAULT_COLOR;
};

// ============================================
// Component
// ============================================

const ColorPickerInputComponent: React.FC<ColorPickerInputProps> = ({
  name = "color",
  value,
  hideValue = false,
  onChange,
  palette = DEFAULT_COLOR_PALETTE,
  label,
  description,
  error,
  disabled = false,
  size = "md",
  required = false,
  allowTransparent = false,
  allowGradient = false,
}) => {
  const { t } = useTranslation(["common"]);

  const [opened, setOpened] = useState(false);
  // Working copy — edited freely in the modal; the saved `value` is only replaced
  // on Apply, so experimenting never loses the current colour.
  const [draft, setDraft] = useState("");
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);
  const [editingStop, setEditingStop] = useState(0);

  const height = INPUT_HEIGHT[size];
  const iconSize = ICON_SIZE[size];
  const presets = palette as string[];

  // Seed the draft from the committed value each time the editor opens.
  useEffect(() => {
    if (!opened) return;
    const seed = value && value.trim() ? value : (presets[0] ?? DEFAULT_COLOR);
    setDraft(seed);
    setText(seed);
    setEditingStop(0);
  }, [opened, value, presets]);

  // Keep the copy/paste box in sync as the draft changes elsewhere.
  useEffect(() => {
    setText(draft);
  }, [draft]);

  // ── Row preview (committed value) ─────────────────────────────────────────
  const rowGradient = allowGradient && isGradient(value);
  const rowSolid = useMemo(() => parseSolid(value), [value]);
  const rowContrast = useMemo(
    () => (rowGradient || !value ? "#fff" : getMostContrastingColor(rowSolid.hex)),
    [rowGradient, value, rowSolid.hex],
  );

  // ── Draft derivations (what the modal edits) ──────────────────────────────
  const draftGradient = allowGradient && isGradient(draft);
  const solid = useMemo(() => parseSolid(draft), [draft]);
  const grad = useMemo(() => (draftGradient ? parseGradient(draft) : null), [draftGradient, draft]);

  const apply = useCallback((v: string) => setDraft(v), []);

  const handleShuffle = useCallback(() => {
    if (disabled) return;
    onChange(name, getRandomPaletteColor(palette, value));
  }, [disabled, palette, value, onChange, name]);

  const setAlpha = (a: number) => apply(withAlpha(solid.hex, a));

  const switchMode = (mode: string) => {
    if (mode === "gradient") {
      apply(buildGradient("linear", 90, [
        { color: solid.hex, position: 0 },
        { color: "#FFFFFF", position: 100 },
      ]));
      setEditingStop(0);
    } else {
      apply(solid.hex);
    }
  };

  const patchGradient = (next: Partial<{ type: "linear" | "radial"; angle: number; stops: GradientStop[] }>) => {
    if (!grad) return;
    const merged = { ...grad, ...next };
    apply(buildGradient(merged.type, merged.angle, merged.stops));
  };
  const setStop = (i: number, patch: Partial<GradientStop>) => {
    if (!grad) return;
    patchGradient({ stops: grad.stops.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  };
  const addStop = () => {
    if (!grad || grad.stops.length >= 5) return;
    // Land the new stop at (or after) the last one — positions stay ascending.
    const maxPos = grad.stops.reduce((m, s) => Math.max(m, s.position), 0);
    patchGradient({ stops: [...grad.stops, { color: getRandomPaletteColor(palette), position: Math.min(100, maxPos) }] });
    setEditingStop(grad.stops.length);
  };
  const removeStop = (i: number) => {
    if (!grad || grad.stops.length <= 2) return;
    patchGradient({ stops: grad.stops.filter((_, idx) => idx !== i) });
    setEditingStop(0);
  };
  // Duplicate a stop's colour into a new stop right after it — positioned midway
  // to the next stop (or just past it if it is last).
  const duplicateStop = (i: number) => {
    if (!grad || grad.stops.length >= 5) return;
    const cur = grad.stops[i];
    const next = grad.stops[i + 1];
    const position = next ? Math.round((cur.position + next.position) / 2) : Math.min(100, cur.position + 10);
    const copy = { color: cur.color, position };
    patchGradient({ stops: [...grad.stops.slice(0, i + 1), copy, ...grad.stops.slice(i + 1)] });
    setEditingStop(i + 1);
  };
  // Mirror the gradient: each colour moves to (100 − its position), re-sorted
  // ascending — so the whole sequence runs the other way.
  const reverseGradient = () => {
    if (!grad) return;
    patchGradient({
      stops: grad.stops
        .map((s) => ({ color: s.color, position: 100 - s.position }))
        .sort((a, b) => a.position - b.position),
    });
    setEditingStop(0);
  };

  const commitText = () => {
    const raw = text.trim();
    if (!raw) return;
    if (allowGradient && isGradient(raw)) {
      apply(raw);
      return;
    }
    const parsed = normalizeColor(raw);
    if (parsed) apply(allowTransparent ? withAlpha(parsed.hex, parsed.alpha) : parsed.hex);
    else setText(draft); // revert on garbage
  };
  const copy = () => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(draft || "").then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      });
    }
  };

  const confirm = () => {
    onChange(name, draft);
    setOpened(false);
  };

  return (
    <InputWrapper label={label} description={description} error={error} required={required} size={size}>
      <Group gap="xs" align="stretch" wrap="nowrap" mt={4}>
        {/* Preview — styled as an input field (renders gradients + transparency). */}
        <Box
          style={{
            flex: 1,
            height,
            borderRadius: "var(--mantine-radius-default)",
            background: CHECKER,
            border: error ? "1px solid var(--mantine-color-red-6)" : "1px solid var(--mantine-color-gray-4)",
            opacity: disabled ? 0.6 : 1,
            minWidth: 0,
            overflow: "hidden",
            position: "relative",
            cursor: disabled ? undefined : "pointer",
          }}
          onClick={() => !disabled && setOpened(true)}
        >
          <Box style={{ position: "absolute", inset: 0, background: value || "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {!hideValue && !rowGradient && (
              <Text size="md" ff="monospace" c={rowContrast} fw={500}>
                {value || DEFAULT_COLOR}
              </Text>
            )}
          </Box>
        </Box>

        {/* Shuffle */}
        <Tooltip label={t("common:colorPicker.random", { defaultValue: "Random color" })} withArrow position="top">
          <ActionIcon variant="default" size={height} onClick={handleShuffle} disabled={disabled} aria-label={t("common:colorPicker.random", { defaultValue: "Random color" })} style={{ flexShrink: 0 }}>
            <AppIcon iconName="Refresh" size={iconSize * 0.5} />
          </ActionIcon>
        </Tooltip>

        {/* Open editor */}
        <Tooltip label={t("common:colorPicker.pick", { defaultValue: "Pick color" })} withArrow position="top">
          <ActionIcon variant="default" size={height} onClick={() => setOpened(true)} disabled={disabled} aria-label={t("common:colorPicker.pick", { defaultValue: "Pick color" })} style={{ flexShrink: 0 }}>
            <AppIcon iconName="Palette" size={iconSize * 0.5} />
          </ActionIcon>
        </Tooltip>
      </Group>
      {!hideValue && rowGradient && (
        <Text size="xs" c="dimmed" mt={4} lineClamp={1}>
          {value}
        </Text>
      )}

      <StackedModal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t("common:colorPicker.choose", { defaultValue: "Choose a color" })}
        centered
        size={360}
        zIndex={1200}
        overlayProps={{ backgroundOpacity: 0.35 }}
      >
        <Stack gap="sm">
          {allowGradient && (
            <SegmentedControl
              fullWidth
              value={draftGradient ? "gradient" : "solid"}
              onChange={switchMode}
              data={[
                { value: "solid", label: t("common:colorPicker.solid", { defaultValue: "Solid" }) },
                { value: "gradient", label: t("common:colorPicker.gradient", { defaultValue: "Gradient" }) },
              ]}
            />
          )}

          {draftGradient && grad ? (
            <>
              <SegmentedControl
                fullWidth
                value={grad.type}
                onChange={(v) => patchGradient({ type: v as "linear" | "radial" })}
                data={[
                  { value: "linear", label: t("common:colorPicker.linear", { defaultValue: "Linear" }) },
                  { value: "radial", label: t("common:colorPicker.radial", { defaultValue: "Radial" }) },
                ]}
              />
              {grad.type === "linear" && (
                <NumberInput
                  label={t("common:colorPicker.angle", { defaultValue: "Angle" })}
                  value={grad.angle}
                  onChange={(v) => patchGradient({ angle: typeof v === "number" ? v : 90 })}
                  min={0}
                  max={360}
                  suffix="°"
                />
              )}
              <Group justify="space-between">
                <Text fw={500}>{t("common:colorPicker.stops", { defaultValue: "Color stops" })}</Text>
                <Group gap={4}>
                  <Tooltip label={t("common:colorPicker.reverse", { defaultValue: "Reverse" })} withArrow>
                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={reverseGradient} aria-label={t("common:colorPicker.reverse", { defaultValue: "Reverse" })}>
                      <AppIcon iconName="ArrowsLeftRight" size={15} />
                    </ActionIcon>
                  </Tooltip>
                  {grad.stops.length < 5 && (
                    <ActionIcon size="sm" variant="light" onClick={addStop} aria-label={t("common:buttons.add", { defaultValue: "Add" })}>
                      <AppIcon iconName="Plus" size={14} />
                    </ActionIcon>
                  )}
                </Group>
              </Group>
              {grad.stops.map((s, i) => (
                <Group key={i} gap="xs" wrap="nowrap">
                  <ColorSwatch color={s.color} size={26} onClick={() => setEditingStop(i)} style={{ cursor: "pointer", outline: editingStop === i ? "2px solid var(--mantine-color-brand-5)" : undefined, flexShrink: 0 }} />
                  <NumberInput value={s.position} onChange={(v) => setStop(i, { position: typeof v === "number" ? v : 0 })} min={0} max={100} suffix="%" style={{ flex: 1 }} />
                  {grad.stops.length < 5 && (
                    <Tooltip label={t("common:colorPicker.duplicate", { defaultValue: "Duplicate" })} withArrow>
                      <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => duplicateStop(i)} aria-label={t("common:colorPicker.duplicate", { defaultValue: "Duplicate" })}>
                        <AppIcon iconName="Copy" size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {grad.stops.length > 2 && (
                    <ActionIcon size="sm" color="red" variant="subtle" onClick={() => removeStop(i)} aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}>
                      <AppIcon iconName="X" size={14} />
                    </ActionIcon>
                  )}
                </Group>
              ))}
              <ColorPicker value={grad.stops[editingStop]?.color ?? "#FFFFFF"} onChange={(c) => setStop(editingStop, { color: c.toUpperCase() })} format={allowTransparent ? "hexa" : "hex"} fullWidth swatches={presets} swatchesPerRow={8} />
            </>
          ) : (
            <>
              <ColorPicker
                value={draft && !isGradient(draft) ? (allowTransparent ? withAlpha(solid.hex, solid.alpha) : solid.hex) : solid.hex}
                onChange={(c) => {
                  const p = parseSolid(c);
                  apply(allowTransparent ? withAlpha(p.hex, p.alpha) : p.hex);
                }}
                format={allowTransparent ? "hexa" : "hex"}
                fullWidth
                swatches={presets}
                swatchesPerRow={8}
              />
              {allowTransparent && (
                <Button size="compact-sm" variant="light" color="gray" leftSection={<AppIcon iconName="Eraser" size={14} />} onClick={() => setAlpha(0)} style={{ alignSelf: "flex-start" }}>
                  {t("common:colorPicker.transparent", { defaultValue: "Transparent" })}
                </Button>
              )}
            </>
          )}

          {/* Live preview of the draft */}
          <Box style={{ height: 44, borderRadius: 8, border: "1px solid var(--mantine-color-gray-4)", background: CHECKER, overflow: "hidden" }}>
            <Box style={{ width: "100%", height: "100%", background: draft || "transparent" }} />
          </Box>

          {/* Copy / paste any format */}
          <TextInput
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
            onBlur={commitText}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitText();
              }
            }}
            placeholder="#4C6EF5"
            rightSection={
              <Tooltip label={copied ? t("common:buttons.copied", { defaultValue: "Copied" }) : t("common:buttons.copy", { defaultValue: "Copy" })} withArrow>
                <ActionIcon variant="subtle" color={copied ? "green" : "gray"} onClick={copy} aria-label={t("common:buttons.copy", { defaultValue: "Copy" })}>
                  <AppIcon iconName={copied ? "Check" : "Copy"} size={16} />
                </ActionIcon>
              </Tooltip>
            }
          />

          <Group justify="flex-end" gap="sm" mt="xs">
            <Button variant="default" onClick={() => setOpened(false)}>
              {t("common:buttons.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button onClick={confirm}>
              {t("common:buttons.apply", { defaultValue: "Apply" })}
            </Button>
          </Group>
        </Stack>
      </StackedModal>
    </InputWrapper>
  );
};

export const ColorPickerInput = React.memo(ColorPickerInputComponent);
